import { defineStore } from 'pinia';
import { ApiV2Error, createApiV2Client } from '../services/apiV2.js';

const config = {
    apiRoot: null,
    fetchImpl: undefined,
    idempotencyKeyFactory: () =>
        globalThis.crypto?.randomUUID?.() ||
        `surveillance-${Date.now()}-${Math.random().toString(36).slice(2)}`,
};

export function configureSurveillanceApi(options = {}) {
    Object.assign(config, options);
}

function client() {
    return createApiV2Client({
        apiRoot: config.apiRoot,
        fetchImpl: config.fetchImpl,
    });
}

function sitePath(siteId, caseId, suffix = 'closure-context') {
    return `/sites/${encodeURIComponent(
        siteId
    )}/surveillance-cases/${encodeURIComponent(caseId)}/${suffix}`;
}

function requiredId(value, label) {
    if (value === null || value === undefined || value === '') {
        throw new TypeError(`${label} is required.`);
    }
    return value;
}

function mutationPayload(action, input = {}) {
    const reason = String(input.reason || '').trim();
    if (!reason) throw new TypeError('A closure reason is required.');
    const payload = {
        reason,
        antimicrobialReviewConsidered:
            input.antimicrobialReviewConsidered === true,
    };
    if (action === 'resolve') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(input.resolvedDate || ''))) {
            throw new TypeError('A resolved date is required.');
        }
        payload.resolvedDate = input.resolvedDate;
    }
    return payload;
}

export const useSurveillanceStore = defineStore('surveillance', {
    state: () => ({
        closureContext: null,
        closureSiteId: null,
        closureCaseId: null,
        closureEtag: null,
        closureLoading: false,
        closureMutating: false,
        closureError: null,
        requestToken: 0,
        idempotencySignature: '',
        idempotencyKey: '',
    }),
    actions: {
        async loadClosureContext(siteId, caseId) {
            requiredId(siteId, 'Site');
            requiredId(caseId, 'Surveillance case');
            const token = ++this.requestToken;
            this.closureLoading = true;
            this.closureError = null;
            try {
                const response = await client().request(
                    sitePath(siteId, caseId),
                    { includeResponseMeta: true }
                );
                if (token !== this.requestToken) return null;
                this.closureContext = response.data;
                this.closureSiteId = String(siteId);
                this.closureCaseId = String(caseId);
                this.closureEtag = response.etag || null;
                return response.data;
            } catch (error) {
                if (token !== this.requestToken) return null;
                this.closureError = error;
                throw error;
            } finally {
                if (token === this.requestToken) this.closureLoading = false;
            }
        },
        async closeCase(action, siteId, caseId, input = {}) {
            if (!['resolve', 'delete'].includes(action)) {
                throw new TypeError('Unsupported surveillance closure action.');
            }
            requiredId(siteId, 'Site');
            requiredId(caseId, 'Surveillance case');
            const context = this.closureContext;
            if (!context) {
                throw new TypeError(
                    'Closure context must be loaded before mutation.'
                );
            }
            if (
                this.closureSiteId !== String(siteId) ||
                this.closureCaseId !== String(caseId)
            ) {
                throw new TypeError(
                    'Closure context does not match this Surveillance case.'
                );
            }
            if (
                context?.acknowledgementRequired &&
                input.antimicrobialReviewConsidered !== true
            ) {
                throw new TypeError(
                    'Antimicrobial review acknowledgement is required.'
                );
            }
            const token = this.requestToken;
            const payload = mutationPayload(action, input);
            const signature = `${action}:${siteId}:${caseId}:${JSON.stringify(
                payload
            )}`;
            if (signature !== this.idempotencySignature) {
                this.idempotencySignature = signature;
                this.idempotencyKey = config.idempotencyKeyFactory(signature);
            }
            this.closureMutating = true;
            this.closureError = null;
            try {
                const response = await client().request(
                    sitePath(
                        siteId,
                        caseId,
                        action === 'resolve' ? 'resolution' : 'deletion'
                    ),
                    {
                        method: 'POST',
                        body: payload,
                        ifMatch: this.closureEtag,
                        idempotencyKey: this.idempotencyKey,
                        includeResponseMeta: true,
                    }
                );
                if (token !== this.requestToken) return null;
                this.closureEtag = response.etag || this.closureEtag;
                return response.data;
            } catch (error) {
                if (token !== this.requestToken) return null;
                this.closureError = error;
                throw error;
            } finally {
                if (token === this.requestToken) {
                    this.closureMutating = false;
                }
            }
        },
        clearClosure() {
            this.requestToken += 1;
            this.closureContext = null;
            this.closureSiteId = null;
            this.closureCaseId = null;
            this.closureEtag = null;
            this.closureError = null;
            this.closureLoading = false;
            this.closureMutating = false;
            this.idempotencySignature = '';
            this.idempotencyKey = '';
        },
    },
});

export { ApiV2Error, mutationPayload, sitePath };
